import React, { useState } from "react";
import "./RegisterPage.css";
import { addEmailToList } from '../emailSignup';

const RegisterPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    device: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // iPhone models - you can update this list as needed
  const iPhoneModels = [
    { value: "", label: "Select your iPhone model" },
    { value: "iphone-17-pro-max", label: "iPhone 17 Pro Max" },
    { value: "iphone-17-pro", label: "iPhone 17 Pro" },
    { value: "iphone-air", label: "iPhone Air" },
    { value: "iphone-17", label: "iPhone 17" },
    { value: "iphone-16-e", label: "iPhone 16e" },
    { value: "iphone-16-pro-max", label: "iPhone 16 Pro Max" },
    { value: "iphone-16-pro", label: "iPhone 16 Pro" },
    { value: "iphone-16-plus", label: "iPhone 16 Plus" },
    { value: "iphone-16", label: "iPhone 16" },
    { value: "iphone-15-pro-max", label: "iPhone 15 Pro Max" },
    { value: "iphone-15-pro", label: "iPhone 15 Pro" },
    { value: "iphone-15-plus", label: "iPhone 15 Plus" },
    { value: "iphone-15", label: "iPhone 15" },
    { value: "iphone-14-pro-max", label: "iPhone 14 Pro Max" },
    { value: "iphone-14-pro", label: "iPhone 14 Pro" },
    { value: "iphone-14-plus", label: "iPhone 14 Plus" },
    { value: "iphone-14", label: "iPhone 14" },
    { value: "iphone-13-pro-max", label: "iPhone 13 Pro Max" },
    { value: "iphone-13-pro", label: "iPhone 13 Pro" },
    { value: "iphone-13", label: "iPhone 13" },
    { value: "iphone-13-mini", label: "iPhone 13 mini" },
    { value: "iphone-12-pro-max", label: "iPhone 12 Pro Max" },
    { value: "iphone-12-pro", label: "iPhone 12 Pro" },
    { value: "iphone-12", label: "iPhone 12" },
    { value: "iphone-12-mini", label: "iPhone 12 mini" },
    { value: "iphone-11-pro-max", label: "iPhone 11 Pro Max" },
    { value: "iphone-11-pro", label: "iPhone 11 Pro" },
    { value: "iphone-11", label: "iPhone 11" },
    { value: "iphone-se-3", label: "iPhone SE (3rd gen)" },
    { value: "iphone-se-2", label: "iPhone SE (2nd gen)" },
    { value: "other", label: "Other/Older Model" },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    } else if (formData.name.trim().length < 2) {
      newErrors.name = "Name must be at least 2 characters";
    } else if (formData.name.trim().length > 100) {
      newErrors.name = "Name must be less than 100 characters";
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!emailRegex.test(formData.email.trim())) {
      newErrors.email = "Please enter a valid email address";
    }

    // Device validation
    if (!formData.device) {
      newErrors.device = "Please select your iPhone model";
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Reset states
    setSubmitSuccess(false);
    setErrors({});

    // Validate form
    const validationErrors = validateForm();

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      // Scroll to first error
      const firstErrorField = Object.keys(validationErrors)[0];
      const errorElement = document.querySelector(
        `[name="${firstErrorField}"]`
      );
      if (errorElement) {
        errorElement.focus();
        errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }

    // Form is valid - proceed with submission
    setIsSubmitting(true);

    try {
      // Prepare the complete user data
      const userData = {
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        device: formData.device,
        timestamp: new Date().toISOString(),
      };

      // Call the addEmailToList function with all user data
      const result = await addEmailToList(userData);

      if (result.success) {
        // Success handling
        setSubmitSuccess(true);

        // Reset form after successful submission
        setTimeout(() => {
          setFormData({
            name: "",
            email: "",
            device: "",
          });
          // Hide success message after 5 seconds
          setTimeout(() => {
            setSubmitSuccess(false);
          }, 5000);
        }, 2000);
      } else {
        // Handle specific error messages from the function
        setErrors({
          submit: result.message || "Something went wrong. Please try again.",
        });
      }
    } catch (error) {
      console.error("Submission error:", error);
      setErrors({
        submit: "Something went wrong. Please try again later.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="register-page">
      <div className="register-container">
        {/* Header */}
        <div className="register-header">
          <h1>Join the Beta</h1>
          <p>
            Become a beta tester and get free, early access to new features and exercises.
          </p>
        </div>

        {/* Main form */}
        <form className="register-form glass-container" onSubmit={handleSubmit}>
          {/* Success Message */}
          {submitSuccess && (
            <div className="success-message">
              <span className="success-icon">✓</span>
              <p>Thank you for registering! We'll be in touch soon.</p>
            </div>
          )}

          {/* General error message */}
          {errors.submit && (
            <div className="error-message">
              <p>{errors.submit}</p>
            </div>
          )}

          {/* Name Field */}
          <div className="form-group">
            <label htmlFor="name">Full Name</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter your full name"
              className={errors.name ? "input-error" : ""}
              disabled={isSubmitting}
            />
            {errors.name && <span className="field-error">{errors.name}</span>}
          </div>

          {/* Email Field */}
          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="your@email.com"
              className={errors.email ? "input-error" : ""}
              disabled={isSubmitting}
            />
            {errors.email && (
              <span className="field-error">{errors.email}</span>
            )}
            <p className="input-subtitle">Provide the email address you use for your Apple Account.</p>
          </div>

          {/* Device Selection */}
          <div className="form-group">
            <label htmlFor="device">iPhone Model</label>
            <select
              id="device"
              name="device"
              value={formData.device}
              onChange={handleChange}
              className={errors.device ? "input-error" : ""}
              disabled={isSubmitting}
            >
              {iPhoneModels.map((model) => (
                <option key={model.value} value={model.value}>
                  {model.label}
                </option>
              ))}
            </select>
            {errors.device && (
              <span className="field-error">{errors.device}</span>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="submit-button"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span className="spinner"></span>
                <span>Submitting...</span>
              </>
            ) : (
              "Request Access"
            )}
          </button>

          {/* Beta info */}
          <div className="beta-info">
            <p>
              <span className="info-icon">ℹ️</span>
              Lift Coach AI is currently only in beta for iPhones.
            </p>
          </div>
        </form>

        {/* Additional Benefits */}
        <div className="benefits-grid">
          <div className="benefit-card">
            <span className="benefit-icon">🎯</span>
            <h3>Early Access</h3>
            <p>Be first to try new features</p>
          </div>
          <div className="benefit-card">
            <span className="benefit-icon">💎</span>
            <h3>Unlimited Usage</h3>
            <p>No limits on beta testing</p>
          </div>
          <div className="benefit-card">
            <span className="benefit-icon">💬</span>
            <h3>Shape the Product</h3>
            <p>Your feedback drives development</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;